import React, { useEffect, useMemo, useRef } from 'react';

const MANAGED_ATTRIBUTE = 'data-document-head';

const attributeName = (name) => {
    if (name === 'httpEquiv') {
        return 'http-equiv';
    }

    if (name === 'charSet') {
        return 'charset';
    }

    if (name === 'imageSrcSet') {
        return 'imagesrcset';
    }

    if (name === 'imageSizes') {
        return 'imagesizes';
    }

    return name;
};

const setAttributes = (element, props = {}) => {
    Array.from(element.attributes).forEach((attribute) => {
        if (attribute.name !== MANAGED_ATTRIBUTE) {
            element.removeAttribute(attribute.name);
        }
    });

    Object.entries(props).forEach(([name, value]) => {
        if (name === 'children' || value === undefined || value === null || value === false) {
            return;
        }

        element.setAttribute(attributeName(name), value === true ? '' : String(value));
    });
};

const flattenChildren = (children) => React.Children.toArray(children).flatMap((child) => {
    if (!React.isValidElement(child)) {
        return [];
    }

    if (child.type === React.Fragment) {
        return flattenChildren(child.props.children);
    }

    return [child];
});

const getTextContent = (children) => React.Children.toArray(children).join('');

const escapeSelectorValue = (value) => {
    if (window.CSS?.escape) {
        return window.CSS.escape(String(value));
    }

    return String(value).replace(/["\\]/g, '\\$&');
};

const getMetaSelector = (props) => {
    if (props.name) {
        return `meta[name="${escapeSelectorValue(props.name)}"]`;
    }

    if (props.property) {
        return `meta[property="${escapeSelectorValue(props.property)}"]`;
    }

    if (props.httpEquiv) {
        return `meta[http-equiv="${escapeSelectorValue(props.httpEquiv)}"]`;
    }

    if (props.charSet) {
        return `meta[charset]`;
    }

    return null;
};

const getLinkSelector = (props) => {
    if (props.rel === 'canonical') {
        return 'link[rel="canonical"]';
    }

    if (props.rel && props.href) {
        return `link[rel="${escapeSelectorValue(props.rel)}"][href="${escapeSelectorValue(props.href)}"]`;
    }

    return null;
};

export const Helmet = ({ children }) => {
    const idRef = useRef(`head-${Math.random().toString(36).slice(2)}`);
    const headElements = useMemo(() => flattenChildren(children), [children]);

    useEffect(() => {
        const headId = idRef.current;
        const dynamicElements = [];

        // The server pre-renders SEO tags into the shell and marks them
        // data-ssr-seo (see seo/page-seo.js). Once the app hydrates, the React
        // head below becomes the source of truth, so remove the server copies
        // first to avoid duplicate canonical/hreflang/OG/JSON-LD tags. No-op on
        // the dev server and on client-side navigations (nothing is marked).
        document.head.querySelectorAll('[data-ssr-seo]').forEach((element) => element.remove());

        headElements.forEach((child) => {
            const { type, props } = child;

            if (type === 'title') {
                document.title = getTextContent(props.children);
                return;
            }

            if (type === 'html') {
                if (props.lang) {
                    document.documentElement.lang = props.lang;
                }
                return;
            }

            if (type === 'meta') {
                const selector = getMetaSelector(props);
                const element = selector
                    ? document.head.querySelector(selector) || document.createElement('meta')
                    : document.createElement('meta');

                setAttributes(element, props);

                if (!element.parentNode) {
                    document.head.appendChild(element);
                }
                return;
            }

            if (type === 'link') {
                const selector = props.rel === 'canonical' ? getLinkSelector(props) : null;
                const element = selector
                    ? document.head.querySelector(selector) || document.createElement('link')
                    : document.createElement('link');

                setAttributes(element, props);

                if (!element.parentNode) {
                    document.head.appendChild(element);
                }

                if (props.rel !== 'canonical') {
                    element.setAttribute(MANAGED_ATTRIBUTE, headId);
                    dynamicElements.push(element);
                }
                return;
            }

            if (type === 'script') {
                const element = document.createElement('script');
                setAttributes(element, props);
                element.textContent = getTextContent(props.children);
                element.setAttribute(MANAGED_ATTRIBUTE, headId);
                document.head.appendChild(element);
                dynamicElements.push(element);
            }
        });

        return () => {
            dynamicElements.forEach((element) => {
                if (element.getAttribute(MANAGED_ATTRIBUTE) === headId) {
                    element.remove();
                }
            });
        };
    }, [headElements]);

    return null;
};
